import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import { Checkbox, Tooltip } from "@mui/material";
import { UseMutationResult } from "@tanstack/react-query";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { Duration } from "moment";
import "moment-duration-format";
import type { Campaign } from "@/pages/mill/schedule/models/Campaign";

const moment = getMomentTz();

const CheckboxCellRenderer = ({
	value,
	disabled,
	onChange,
}: {
	value: boolean;
	disabled: boolean;
	onChange: (checked: boolean) => void;
}) => {
	return (
		<Checkbox
			checked={value}
			disabled={disabled}
			onChange={(_e, checked) => onChange(checked)}
		/>
	);
};

export const useM1TableData = ({
	sampleStatusMutation,
	isLoading,
}: {
	sampleStatusMutation: UseMutationResult<
		void,
		Error,
		{
			heatId: number;
			value: "P" | "R" | "U";
		},
		unknown
	>;
	isLoading: boolean;
}) => {
	const columnDefs: ColDef[] = [
		{
			field: "CampaignNumber",
			headerName: "Number",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "SectionName",
			headerName: "Section",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "HeatName",
			headerName: "Heat",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "Start",
			headerName: "Start",
			minWidth: 20,
			flex: 2,
			valueGetter: (params) =>
				dxtToLocalServerTime((params.data as Campaign)?.Start, "MM/dd HH:mm") ||
				"",
		},
		{
			field: "End",
			headerName: "End",
			minWidth: 20,
			flex: 2,
			valueGetter: (params) =>
				dxtToLocalServerTime((params.data as Campaign)?.End, "MM/dd HH:mm") ||
				"-",
		},
		{
			field: "Duration",
			headerName: "Duration",
			minWidth: 20,
			flex: 2,
			valueFormatter: (params) => {
				if (params.value == null) return "";
				if (params.value === 0) return "0m";
				const duration = moment.duration(params.value, "seconds");
				return duration.format("w[w] d[d] h[h] m[m]", { trim: "both" });
			},
		},
		{
			field: "RolledColdTons",
			headerName: "Rolled Tons",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => {
				if (params.value) {
					return params.value.toFixed(2);
				}
				return "";
			},
			cellStyle: {
				justifyContent: "flex-end",
			},
		},
		{
			field: "RolledBillets",
			headerName: "Rolled Bill.",
			minWidth: 70,
			flex: 1,
			cellStyle: {
				justifyContent: "flex-end",
			},
		},
		{
			field: "TonsPerHour",
			headerName: "Tons/Hr",
			minWidth: 20,
			flex: 1,
			valueGetter: (params) =>
				(params.data as Campaign)?.TonsPerHour?.toFixed(2),
		},
		{
			field: "TotalDelayTime",
			headerName: "Delays",
			minWidth: 20,
			flex: 2,
			valueFormatter: (params) => {
				if (params.value == null) return "";
				const duration = moment.duration(params.value, "seconds") as Duration;
				return `${duration.format("HH:mm:ss", { trim: false })}`;
			},
		},
		{
			field: "DelayTimePercent",
			headerName: "Delays %",
			minWidth: 20,
			flex: 1,
			valueFormatter: (params) => {
				if (params.value == null) return "";
				return `${(params.data as Campaign).DelayTimePercent?.toFixed(0)}%`;
			},
		},
		{
			field: "Cobbles",
			headerName: "Cobbles",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "Passed",
			headerName: "Passed",
			minWidth: 40,
			flex: 1,
			valueGetter: (params) =>
				(params.data as Campaign).SampleStatusCode == "P",
			cellRenderer: (params: ICellRendererParams) => (
				<CheckboxCellRenderer
					value={params.value}
					disabled={isLoading}
					onChange={(checked) => {
						if (checked) {
							sampleStatusMutation.mutate({
								heatId: (params.data as Campaign).HeatId!,
								value: "P",
							});
						} else {
							sampleStatusMutation.mutate({
								heatId: (params.data as Campaign).HeatId!,
								value: "U",
							});
						}
					}}
				/>
			),
		},
		{
			field: "ReTest",
			headerName: "Re-Test",
			minWidth: 40,
			flex: 1,
			valueGetter: (params) =>
				(params.data as Campaign).SampleStatusCode == "R",
			cellRenderer: (params: ICellRendererParams) => (
				<CheckboxCellRenderer
					value={params.value}
					disabled={isLoading}
					onChange={(checked) => {
						if (checked) {
							sampleStatusMutation.mutate({
								heatId: (params.data as Campaign).HeatId!,
								value: "R",
							});
						} else {
							sampleStatusMutation.mutate({
								heatId: (params.data as Campaign).HeatId!,
								value: "U",
							});
						}
					}}
				/>
			),
		},
	];
	return { columnDefs };
};
